import {
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Typography,
} from '@bb-ui/react-library';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useStyles } from './UserIlluminateRoles.styles';
import {
    UserIlluminateRoleCheckbox,
    UserIlluminateRoleCheckBoxProps,
} from './UserIlluminateRoleCheckbox';
import { useUser } from '../../../../../hooks/useUser';
import {
    ADMIN_ROLE_NAME,
    AUTHOR_ROLE_NAME,
    DEVELOPER_ROLE_NAME,
    VIEWER_ROLE_NAME,
} from '../../roles';
import { useLicenseManagementContext } from '../../../../../contexts/LicenseManagementContext';

interface UserIlluminateRolesProps {
    userId: string;
    selectedRoles: string[];
    initialRoles: string[];
    onRolesChange: (selectedRoles: string[]) => void;
}

export const UserIlluminateRoles: React.FunctionComponent<
    UserIlluminateRolesProps
> = ({ userId, selectedRoles, initialRoles, onRolesChange }) => {
    const sessionUser = useUser();
    const { t } = useTranslation();
    const classes = useStyles();

    const managedRoles = [
        ADMIN_ROLE_NAME,
        AUTHOR_ROLE_NAME,
        DEVELOPER_ROLE_NAME,
        VIEWER_ROLE_NAME,
    ];

    const { seatsUsed, quotas } = useLicenseManagementContext();
    const canAssignAuthorRole =
        initialRoles.includes(AUTHOR_ROLE_NAME) ||
        (seatsUsed && quotas && seatsUsed.author < quotas.author);
    const canAssignViewerRole =
        initialRoles.includes(ADMIN_ROLE_NAME) ||
        initialRoles.includes(VIEWER_ROLE_NAME) ||
        (seatsUsed && quotas && seatsUsed.reader < quotas.reader);

    type ManagedRoleProps = Pick<
        UserIlluminateRoleCheckBoxProps,
        'disabled' | 'tooltipTitle'
    >;

    const managedRoleProps = (role: string): ManagedRoleProps => {
        if (role === ADMIN_ROLE_NAME && sessionUser?.userId === userId) {
            return {
                disabled: true,
                tooltipTitle: t(
                    'settings.illuminateRoles.adminSelfModificationDisabledReason',
                ),
            };
        }
        if (role === AUTHOR_ROLE_NAME && !canAssignAuthorRole) {
            // prettier-ignore
            return {
                disabled: true,
                tooltipTitle: <Trans i18nKey='settings.illuminateRoles.authorSeatsFullTooltip'>
                    <b>Title </b> Hint
                </Trans>,
            };
        }
        if (
            [ADMIN_ROLE_NAME, VIEWER_ROLE_NAME].includes(role) &&
            !canAssignViewerRole
        ) {
            return {
                disabled: true,
                tooltipTitle: (
                    <Trans i18nKey="settings.illuminateRoles.adminViewerSeatsFullTooltip">
                        <b>Title </b> Hint
                    </Trans>
                ),
            };
        }
        return {};
    };

    const handleRoleChange = (roleName: string, checked: boolean) => {
        let updatedRoles;
        if (checked) {
            updatedRoles = [...selectedRoles, roleName];
        } else {
            updatedRoles = selectedRoles.filter(role => role !== roleName);
        }
        onRolesChange(updatedRoles);
    };

    const renderRoleCheckbox = (roleName: string) => {
        const isChecked = selectedRoles.includes(roleName);
        return (
            <UserIlluminateRoleCheckbox
                key={roleName}
                roleName={roleName}
                checked={isChecked}
                onChange={handleRoleChange}
                {...managedRoleProps(roleName)}
            />
        );
    };

    return (
        <ExpansionPanel
            defaultExpanded={true}
            className={classes.userRolesSection}
        >
            <ExpansionPanelSummary
                id="roles-assigned-summary"
                aria-controls="roles-assigned-details"
            >
                <div className={classes.userRolesSummary}>
                    <Typography variant="h2">
                        {t('settings.illuminateRoles.title')}
                        {/* prettier-ignore */}
                        <span className={classes.roleCount}>
                            {' '}|{' '}{t(
                                'settings.illuminateRoles.rolesAssigned',
                                {
                                    assigned: selectedRoles.filter(
                                        role => managedRoles.includes(role),
                                    ).length,
                                    total: managedRoles.length,
                                },
                            )}
                        </span>
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.illuminateRoles.hint')}
                    </Typography>
                </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails className={classes.userRolesContainer}>
                {managedRoles.map(role => renderRoleCheckbox(role))}
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};
